import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog, DialogActions, DialogContent, DialogTitle, Button, Box,
  FormControlLabel, Checkbox, Snackbar, Alert,
} from '@mui/material';
import { Field } from '../../UI';
import { findFasesEnviarProcessosEmMassa } from '../../../../services/core/fase/fase.services';
import { atualiarFaseMultiplosProcessosService } from '../../../../services/core/processos/processosFaseService';
import {
  findMotivosBaixaAction, findNegociadoresExternosAtivosAction, findPastasFaseAction,
} from '../../../../store/Global/LoadSelectors/loadSelectors.saga';

const CAMPOS_INICIAIS = {
  idFase: '',
  idFasePasta: '',
  idMotivoBaixa: '',
  idNegociador: '',
  liberarRequisicao: true,
  recalcularValorProcesso: true,
  liberarCompromissos: true,
};

const MENSAGEM_ERRO_GENERICA = 'Não foi possível alterar as fases.';
const MENSAGEM_ERRO_CARREGAR_FASES = 'Não foi possível carregar as fases.';

/**
 * Porta de `DialogEnviarProcessosOutraFase.jsx`/`EnviarProcessosOutraFaseController.jsx`
 * (tela real), Etapa 2 de `docs/architecture/plano-acoes-em-massa-grid-processos.md`.
 * Mesmos 4 campos, 3 condicionalidades e 3 checkboxes da tela real — sem
 * `FieldModel` (protótipo usa `useState` local, ver `src/prototipo/CLAUDE.md`).
 *
 * Decisão do usuário: sem reload da grid após sucesso — o único endpoint
 * chamável sem reexecutar a pesquisa inteira (`buscarFasesAndInfoAtuaisProcessos`)
 * não devolve os campos que a grid exibe (`corAlertaFase`/`diasFase`/`pasta`/
 * `requisicao`/`status`), então um patch otimista seria dado especulado, não
 * real. Fecha o dialog e mostra sucesso; grid só atualiza numa nova pesquisa.
 */
export default function DialogEnviarOutraFase({
  open, onClose, onSucesso, processosSelecionados, elementsEqualFase, faseProcessos,
  processosPermitemFaseCobrancaPresencial, processosTemAgendamento, processosTemRequisicao,
}) {
  const dispatch = useDispatch();

  const [fases, setFases] = useState([]);
  const [campos, setCampos] = useState(CAMPOS_INICIAIS);
  const [erros, setErros] = useState({});
  const [erroGenerico, setErroGenerico] = useState('');
  const [enviando, setEnviando] = useState(false);

  const [showNegociadoresExternosSelector, setShowNegociadoresExternosSelector] = useState(false);
  const [showPastasSelector, setShowPastasSelector] = useState(false);
  const [showMotivosSelector, setShowMotivosSelector] = useState(false);

  const fasesOptions = fases.map(fase => ({ id: fase.idFase, value: fase.nome }));
  const negociadoresExternosAtivosSelectorItems = useSelector(states => states.selectors.negociadoresExternosAtivosFase.selector);
  const fasePastasSelectorItems = useSelector(states => states.selectors.pastasDaFase.selector);
  const motivosBaixaSelectorItems = useSelector(states => states.selectors.motivosBaixa.selector);

  useEffect(() => {
    // `DialogEnviarOutraFase` fica montado o tempo todo (o pai não desmonta
    // ao fechar, só alterna `open` para permitir a transição de saída do
    // MUI Dialog) — sem essa guarda, este fetch dispararia a cada montagem
    // do menu, não só quando o dialog realmente abre.
    if (!open) return;
    setErroGenerico('');
    findFasesEnviarProcessosEmMassa(processosPermitemFaseCobrancaPresencial, processosSelecionados.map(String))
      .then(response => setFases(response.data))
      .catch(() => setErroGenerico(MENSAGEM_ERRO_CARREGAR_FASES));
  }, [open, processosPermitemFaseCobrancaPresencial, processosSelecionados]);

  useEffect(() => {
    if (!showNegociadoresExternosSelector) setCampos(fields => ({ ...fields, idNegociador: '' }));
  }, [showNegociadoresExternosSelector]);

  useEffect(() => {
    if (!showPastasSelector) setCampos(fields => ({ ...fields, idFasePasta: '' }));
  }, [showPastasSelector]);

  useEffect(() => {
    if (!showMotivosSelector) setCampos(fields => ({ ...fields, idMotivoBaixa: '' }));
  }, [showMotivosSelector]);

  const onChangeFase = useCallback((event) => {
    const { value } = event.target;
    // `Field` sempre inclui a opção em branco "Selecione" (diferente do
    // `SelectorField` da tela real) — limpar as 3 condicionalidades sem
    // procurar faseInfo quando o usuário limpa a seleção.
    const faseInfo = fases.find(fase => fase.idFase === value);

    setShowNegociadoresExternosSelector(faseInfo?.idTipoNegociador === 3);
    if (faseInfo?.idTipoNegociador === 3) {
      dispatch(findNegociadoresExternosAtivosAction(faseInfo.idFase));
    }

    setShowPastasSelector(faseInfo?.numPastas > 0);
    if (faseInfo?.numPastas > 0) {
      const publico = elementsEqualFase && value === faseProcessos ? null : 'S';
      dispatch(findPastasFaseAction(value, publico));
    }

    setShowMotivosSelector(faseInfo?.exigirMotivo === 'S');
    if (faseInfo?.exigirMotivo === 'S') {
      dispatch(findMotivosBaixaAction());
    }

    setCampos(fields => ({ ...fields, idFase: value }));
  }, [dispatch, fases, faseProcessos, elementsEqualFase]);

  const onChangeCampo = nome => (event) => {
    // React 16 recicla o SyntheticEvent — ler `event.target` dentro do
    // updater funcional de setState arrisca `event.target === null` se o
    // React processar fora do ciclo síncrono do evento (ver
    // DialogEnviarEmailDevedor.jsx, mesmo bug encontrado em produção).
    const { value } = event.target;
    setCampos(fields => ({ ...fields, [nome]: value }));
  };

  const onChangeCheckbox = nome => (event) => {
    const { checked } = event.target;
    setCampos(fields => ({ ...fields, [nome]: checked }));
  };

  const onSubmit = useCallback((event) => {
    event.preventDefault();
    if (enviando) return;
    setEnviando(true);
    const payload = { processos: processosSelecionados.map(String), ...campos };
    atualiarFaseMultiplosProcessosService(payload).then(() => {
      onSucesso('Ações realizadas com sucesso!');
      onClose();
    }).catch((err) => {
      const validations = err.response?.data?.validations;
      if (validations) {
        setErros(validations.reduce((acc, erro) => ({ ...acc, [erro.field]: erro.message }), {}));
      } else {
        setErroGenerico(err.response?.data?.message || MENSAGEM_ERRO_GENERICA);
      }
    }).finally(() => setEnviando(false));
  }, [campos, enviando, processosSelecionados, onClose, onSucesso]);

  const confirmarDesabilitado = enviando
    || !campos.idFase
    || (showNegociadoresExternosSelector && negociadoresExternosAtivosSelectorItems.length > 0 && !campos.idNegociador)
    || (showMotivosSelector && motivosBaixaSelectorItems.length > 0 && !campos.idMotivoBaixa);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Enviar Processos para outra Fase</DialogTitle>
      <form onSubmit={onSubmit}>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Field
              label="Fases*"
              tipo="select"
              options={fasesOptions}
              value={campos.idFase}
              onChange={onChangeFase}
              error={Boolean(erros.idFase)}
              helperText={erros.idFase}
            />
            {showNegociadoresExternosSelector && (
              <Field
                label="Negociador Externo*"
                tipo="select"
                options={negociadoresExternosAtivosSelectorItems}
                value={campos.idNegociador}
                onChange={onChangeCampo('idNegociador')}
                error={Boolean(erros.idNegociador)}
                helperText={erros.idNegociador}
              />
            )}
            {showPastasSelector && fasePastasSelectorItems.length > 0 && (
              <Field
                label="Pasta"
                tipo="select"
                options={fasePastasSelectorItems}
                value={campos.idFasePasta}
                onChange={onChangeCampo('idFasePasta')}
                error={Boolean(erros.idFasePasta)}
                helperText={erros.idFasePasta}
              />
            )}
            {showMotivosSelector && motivosBaixaSelectorItems.length > 0 && (
              <Field
                label="Motivo*"
                tipo="select"
                options={motivosBaixaSelectorItems}
                value={campos.idMotivoBaixa}
                onChange={onChangeCampo('idMotivoBaixa')}
                error={Boolean(erros.idMotivoBaixa)}
                helperText={erros.idMotivoBaixa}
              />
            )}
            <Box>
              <FormControlLabel
                disabled={!processosTemRequisicao}
                control={<Checkbox checked={processosTemRequisicao && campos.liberarRequisicao} />}
                label="Liberar Requisições de Processos"
                onChange={onChangeCheckbox('liberarRequisicao')}
              />
              <FormControlLabel
                disabled={!processosTemAgendamento}
                control={<Checkbox checked={processosTemAgendamento && campos.liberarCompromissos} />}
                label="Liberar compromissos das Agendas dos Processos"
                onChange={onChangeCheckbox('liberarCompromissos')}
              />
              <FormControlLabel
                control={<Checkbox checked={campos.recalcularValorProcesso} />}
                label="Recalcular o valor dos Processos"
                onChange={onChangeCheckbox('recalcularValorProcesso')}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="submit" variant="contained" disabled={confirmarDesabilitado}>Confirmar</Button>
        </DialogActions>
      </form>

      <Snackbar
        open={Boolean(erroGenerico)}
        autoHideDuration={4000}
        onClose={() => setErroGenerico('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" onClose={() => setErroGenerico('')}>
          {erroGenerico}
        </Alert>
      </Snackbar>
    </Dialog>
  );
}

DialogEnviarOutraFase.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSucesso: PropTypes.func.isRequired,
  processosSelecionados: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])).isRequired,
  elementsEqualFase: PropTypes.bool.isRequired,
  faseProcessos: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  processosPermitemFaseCobrancaPresencial: PropTypes.bool.isRequired,
  processosTemAgendamento: PropTypes.bool.isRequired,
  processosTemRequisicao: PropTypes.bool.isRequired,
};

DialogEnviarOutraFase.defaultProps = {
  faseProcessos: null,
};
